import React, { useEffect, useState } from 'react';
import { db, collection, getDocs, query, where, onSnapshot } from '../firebase';
import { Client, Invoice, Branch } from '../types';
import { FileText, Calendar, Building2, Layers, DollarSign, Loader2, Download, Receipt } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';

interface ClientBillingReportProps {
  clients: Client[];
}

export const ClientBillingReport: React.FC<ClientBillingReportProps> = ({ clients }) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('all');
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [branches, setBranches] = useState<Branch[]>([]);
  const [serviceTypes, setServiceTypes] = useState<{ id: string; name: string }[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  
  // Calculations
  const [totalBilled, setTotalBilled] = useState(0);
  const [paidBilled, setPaidBilled] = useState(0);
  const [unpaidBilled, setUnpaidBilled] = useState(0);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);

  // Load all available service types
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'service_types'), (snapshot) => {
      const list: any[] = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, name: doc.data().name });
      });
      setServiceTypes(list);
    });
    return () => unsubscribe();
  }, []);

  // Load client branches when client changes
  useEffect(() => {
    if (!selectedClientId) {
      setBranches([]);
      setSelectedBranchId('all');
      return;
    }

    const loadBranches = async () => {
      try {
        const snap = await getDocs(collection(db, 'clients', selectedClientId, 'branches'));
        const list: Branch[] = [];
        snap.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as Branch);
        });
        setBranches(list);
        setSelectedBranchId('all');
      } catch (e) {
        console.error('Error loading client branches:', e);
      }
    };
    loadBranches();
  }, [selectedClientId]);

  const handleGenerate = async () => {
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }

    setLoading(true);
    try {
      // Query invoices for this client
      const q = query(
        collection(db, 'invoices'),
        where('clientId', '==', selectedClientId)
      );
      const snap = await getDocs(q);
      const list: Invoice[] = [];
      snap.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Invoice);
      });

      // Filter invoices locally based on criteria
      const filtered = list.filter((invoice) => {
        // Date range
        if (startDate) {
          const invDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt as any);
          if (invDate < new Date(startDate)) return false;
        }
        if (endDate) {
          const invDate = invoice.createdAt?.toDate ? invoice.createdAt.toDate() : new Date(invoice.createdAt as any);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (invDate > end) return false;
        }

        // Branch
        if (selectedBranchId !== 'all' && invoice.branchId !== selectedBranchId) {
          return false;
        }

        // Service Type
        if (selectedServiceType !== 'all') {
          // If the invoice has serviceType directly, match it
          if (invoice.serviceType) {
            if (invoice.serviceType !== selectedServiceType) return false;
          } else {
            // Otherwise check if invoice items or description contain service type keyword, or default client service matching
            const matchesDesc = invoice.description?.toLowerCase().includes(selectedServiceType.toLowerCase()) || 
              invoice.items.some(item => item.description.toLowerCase().includes(selectedServiceType.toLowerCase()));
            if (!matchesDesc) return false;
          }
        }

        return true;
      });

      // Sort invoices by date descending
      filtered.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return dateB - dateA;
      });

      // Calculate totals
      let total = 0;
      let paid = 0;
      let unpaid = 0;

      filtered.forEach((inv) => {
        total += inv.totalAmount || 0;
        if (inv.status === 'paid') {
          paid += inv.totalAmount || 0;
        } else if (inv.status !== 'cancelled') {
          unpaid += inv.totalAmount || 0;
        }
      });

      setInvoices(list);
      setFilteredInvoices(filtered);
      setTotalBilled(total);
      setPaidBilled(paid);
      setUnpaidBilled(unpaid);
      setGenerated(true);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating billing report:', error);
      toast.error('Failed to generate report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const client = clients.find(c => c.id === selectedClientId);
    if (!client) return;

    const doc = new jsPDF();
    const currency = client.currency || 'TZS';

    // Corporate Header
    doc.setFillColor(30, 58, 138); // Deep Blue
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 15, 18);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(249, 115, 22); // Orange Accent
    doc.text('General Supplies Limited | Technical Management Solutions', 15, 25);
    doc.setTextColor(255, 255, 255);
    doc.text('P.O. Box, Dar es Salaam, Tanzania', 15, 31);
    doc.text('Email: info@brighttechnical.co.tz | Website: www.brighttechnical.co.tz', 15, 37);

    // Document Title
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT BILLING & SERVICES STATEMENT', 15, 60);

    // Meta Info Column 1
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.text('Client Name:', 15, 72);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(client.name, 45, 72);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Services Offered:', 15, 79);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(client.serviceType || 'Not Specified', 45, 79);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Statement Period:', 15, 86);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    const startText = startDate ? startDate : 'All Time';
    const endText = endDate ? endDate : 'Present';
    doc.text(`${startText} to ${endText}`, 45, 86);

    // Financial Overview Box (Right Side)
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(125, 53, 70, 38, 3, 3, 'FD');
    doc.setDrawColor(226, 232, 240);

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('FINANCIAL SUMMARY', 130, 60);

    doc.setFont('helvetica', 'normal');
    doc.text('Total Billed Amount:', 130, 67);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text(`${currency} ${totalBilled.toLocaleString()}`, 130, 72);

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Paid:', 130, 78);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(22, 163, 74);
    doc.text(`${currency} ${paidBilled.toLocaleString()}`, 130, 83);

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.text('Outstanding Balance:', 130, 89);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text(`${currency} ${unpaidBilled.toLocaleString()}`, 130, 94);

    // List of Branches Section
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT BRANCHES & LOCATIONS', 15, 105);

    const branchHeaders = [['Branch Name', 'Contact Person', 'Email / Phone', 'Address']];
    const branchBody = branches.map(b => [
      b.name,
      (b as any).contactPerson || 'N/A',
      `${(b as any).email || 'N/A'}\n${(b as any).phone || 'N/A'}`,
      b.address || 'N/A'
    ]);

    autoTable(doc, {
      startY: 110,
      head: branchHeaders,
      body: branchBody.length > 0 ? branchBody : [['No branches registered for this client.', '', '', '']],
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 3 },
    });

    // Invoices Section
    const nextY = (doc as any).lastAutoTable.finalY + 15;
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAILED BILLING & INVOICES LIST', 15, nextY);

    const invoiceHeaders = [['Invoice No', 'Date', 'Branch', 'Service Type', 'Status', 'Total Amount']];
    const invoiceBody = filteredInvoices.map(inv => {
      const invDate = inv.createdAt?.toDate ? inv.createdAt.toDate().toLocaleDateString() : 'N/A';
      return [
        inv.invoiceNumber,
        invDate,
        inv.branchName || 'Main Office',
        inv.serviceType || 'Not Specified',
        inv.status.toUpperCase(),
        `${currency} ${inv.totalAmount.toLocaleString()}`
      ];
    });

    autoTable(doc, {
      startY: nextY + 5,
      head: invoiceHeaders,
      body: invoiceBody.length > 0 ? invoiceBody : [['No invoices match the current filters.', '', '', '', '', '']],
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138], fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 3 },
    });

    // Signatures / Base Section (static base footer)
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Horizontal Line
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(15, 275, 195, 275);

      // E & O.E
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text('E & O.E.', 15, 281);

      // Page numbering
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, 180, 281);

      // Disclaimer
      doc.text('This is an official systems generated billing statement compiled by Bright Technical Systems General Supplies Ltd.', 15, 286);
    }

    downloadFile(doc.output('blob'), `client_statement_${client.name.replace(/\s+/g, '_')}.pdf`).then(success => {
      if (success) toast.success('Billing statement PDF generated!');
    });
  };

  return (
    <div className="space-y-8" id="client-billing-report-module">
      <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
        <h2 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
          <Receipt className="w-6 h-6 text-orange-500" />
          <span>Statement Filters</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Client Select */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client *</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
            >
              <option value="">Select a Client</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Branch Select */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch Location</label>
            <select
              disabled={!selectedClientId}
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700 disabled:opacity-50"
            >
              <option value="all">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Service Type Select */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Service Offered</label>
            <select
              disabled={!selectedClientId}
              value={selectedServiceType}
              onChange={(e) => setSelectedServiceType(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700 disabled:opacity-50"
            >
              <option value="all">All Service Types</option>
              {(() => {
                const client = clients.find(c => c.id === selectedClientId);
                const clientServices = client?.serviceType ? client.serviceType.split(',').map(s => s.trim()) : [];
                const list = clientServices.length > 0 ? clientServices : serviceTypes.map(s => s.name);
                const unique = Array.from(new Set(list));
                return unique.map(s => (
                  <option key={s} value={s}>{s}</option>
                ));
              })()}
            </select>
          </div>

          {/* Date Picker Start */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Period From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
            />
          </div>

          {/* Date Picker End */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Period To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-700"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-50">
          <button
            onClick={handleGenerate}
            disabled={loading || !selectedClientId}
            className="px-6 py-3.5 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold shadow-lg shadow-blue-900/25 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                <span>Generate Statement</span>
              </>
            )}
          </button>
        </div>
      </div>

      {generated && (
        <div className="space-y-8 animate-fadeIn">
          {/* Overview KPI Widgets */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Total Billed Amount</span>
                <h3 className="text-2xl font-black text-slate-900">
                  {clients.find(c => c.id === selectedClientId)?.currency || 'TZS'} {totalBilled.toLocaleString()}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Total Amount Paid</span>
                <h3 className="text-2xl font-black text-green-600">
                  {clients.find(c => c.id === selectedClientId)?.currency || 'TZS'} {paidBilled.toLocaleString()}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Outstanding Balance</span>
                <h3 className="text-2xl font-black text-red-600">
                  {clients.find(c => c.id === selectedClientId)?.currency || 'TZS'} {unpaidBilled.toLocaleString()}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Client profile, services offered & Action Buttons */}
          <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-50">
              <div>
                <h3 className="text-xl font-black text-slate-900">Client Statement Report</h3>
                <p className="text-slate-500 font-medium text-xs mt-1">Compiled statement detailing branches and invoice aggregations.</p>
              </div>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-bold shadow-lg shadow-orange-500/25 transition-all cursor-pointer"
              >
                <Download className="w-5 h-5" />
                <span>Export Official PDF</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Client & Services Summary</h4>
                <div className="p-4 bg-slate-50 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Client Name:</span>
                    <span className="text-slate-900 font-black">{clients.find(c => c.id === selectedClientId)?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Services Registered:</span>
                    <span className="text-slate-900 font-black">{clients.find(c => c.id === selectedClientId)?.serviceType || 'None'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Total Branches:</span>
                    <span className="text-slate-900 font-black">{branches.length} Locations</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Statement Specifications</h4>
                <div className="p-4 bg-slate-50 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Filtered Branch:</span>
                    <span className="text-slate-900 font-black">
                      {selectedBranchId === 'all' ? 'All Locations' : branches.find(b => b.id === selectedBranchId)?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Filtered Service:</span>
                    <span className="text-slate-900 font-black">{selectedServiceType === 'all' ? 'All Services' : selectedServiceType}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 font-bold">Period Range:</span>
                    <span className="text-slate-900 font-black">
                      {startDate || 'Beginning'} to {endDate || 'Present'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Branches Directory */}
            <div className="space-y-4 mb-8">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Client Branches & Locations ({branches.length})</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {branches.map((b) => (
                  <div key={b.id} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col justify-between">
                    <div>
                      <h5 className="font-bold text-slate-900 text-sm mb-1">{b.name}</h5>
                      <p className="text-xs font-medium text-slate-500 line-clamp-2">{b.address || 'No address registered'}</p>
                    </div>
                    {((b as any).contactPerson || (b as any).phone || (b as any).email) && (
                      <div className="mt-4 pt-3 border-t border-slate-200/50 text-[11px] font-semibold text-slate-600 space-y-1">
                        {(b as any).contactPerson && <div>Contact: {(b as any).contactPerson}</div>}
                        {(b as any).phone && <div>Phone: {(b as any).phone}</div>}
                        {(b as any).email && <div>Email: {(b as any).email}</div>}
                      </div>
                    )}
                  </div>
                ))}
                {branches.length === 0 && (
                  <div className="p-6 bg-slate-50 border border-slate-100 rounded-2xl text-center text-xs font-medium text-slate-400 italic col-span-full">
                    No branches registered for this client.
                  </div>
                )}
              </div>
            </div>

            {/* Invoices List Table */}
            <div className="space-y-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Aggregated Invoices ({filteredInvoices.length})</h4>
              <div className="border border-slate-100 rounded-2xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Invoice No</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Branch</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Service Type</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredInvoices.map((inv) => {
                      const dateStr = inv.createdAt?.toDate ? inv.createdAt.toDate().toLocaleDateString() : 'N/A';
                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-black text-slate-900">{inv.invoiceNumber}</td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-500">{dateStr}</td>
                          <td className="px-6 py-4 text-sm font-bold text-slate-700">{inv.branchName || 'Main Office'}</td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-500">{inv.serviceType || 'Not Specified'}</td>
                          <td className="px-6 py-4 text-xs font-bold">
                            <span className={`px-2.5 py-1 rounded-full ${
                              inv.status === 'paid' ? 'bg-green-50 text-green-700' :
                              inv.status === 'sent' ? 'bg-blue-50 text-blue-700' :
                              inv.status === 'draft' ? 'bg-slate-100 text-slate-600' :
                              'bg-red-50 text-red-700'
                            }`}>
                              {inv.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-black text-slate-900 text-right">
                            {clients.find(c => c.id === selectedClientId)?.currency || 'TZS'} {inv.totalAmount.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredInvoices.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm font-medium text-slate-400 italic">
                          No invoices found matching these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
